import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { NavLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import BookIcon from '@mui/icons-material/Book';
import EditIcon from '@mui/icons-material/Edit';
import Course from '../components/Course';
import EditCourse from '../components/EditCourse';
import CreateCourseOverview from '../components/CreateCourseOverview';
import Modal from '../components/Modal';
import RightBar from '../components/RightBar';
import Spinner from '../components/spinner/Spinner';
import Calendar from '../components/calendar/Calendar';
import usePermissions from '../hooks/usePermissions';
import MainContent from '../components/MainContent';
import useGetUserRole from '../hooks/useGetUserRole';
import useFetchDatav2 from '../hooks/useFetchDatav2';

function Modules() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const permissions = usePermissions();
  const role = useGetUserRole();

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const toggleEditModal = () => {
    setEditModalOpen(!editModalOpen);
  };

  const { data: courseData, isLoading: courseLoading } = useFetchDatav2(
    ['course', courseId],
    `/courses/${courseId}`,
    {},
    "Couldn't fetch course details.",
    true
  );

  const { data, isLoading } = useFetchDatav2(
    ['course-modules', courseId],
    `/course-modules`,
    {
      params: { courseId },
    },
    "Couldn't get course modules. Please try again.",
    true
  );

  const courseDetails = courseData?.data?.course;
  const canUpdateCourse =
    permissions?.includes('UpdateCourse') || role === 'SuperAdmin';

  return (
    <>
      <MainContent>
        <div className="bg-white px-2 py-5 rounded-lg flex justify-between">
          <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNextIcon />}>
            <NavLink
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
              color="inherit"
              to="/"
              className="flex items-center"
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              <p>Home</p>
            </NavLink>
            <NavLink
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
              color="inherit"
              to="/courses"
              className="flex items-center"
            >
              <BookIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              <p>Courses</p>
            </NavLink>
            {courseDetails?.courseName && (
              <p className="text-gray-500">{courseDetails.courseName}</p>
            )}
          </Breadcrumbs>
          <div className="flex items-center gap-2">
            {canUpdateCourse && (
              <button
                type="button"
                className="border-[1px] border-claret-500 text-claret-500 text-sm py-1 px-4 rounded-md cursor-pointer flex items-center gap-1"
                onClick={toggleEditModal}
              >
                <EditIcon fontSize="inherit" />
                Edit Course
              </button>
            )}
            {(permissions?.includes('CreateCourseModule') ||
              role === 'SuperAdmin') && (
              <button
                type="button"
                className="bg-claret-500 text-white text-sm py-1 px-5 rounded-md cursor-pointer"
                onClick={() => navigate(`/courses/${courseId}/addmodule`)}
              >
                Add Module
              </button>
            )}
          </div>
        </div>
        {courseLoading ? (
          <div className="grid items-center justify-center my-auto mt-4">
            <Spinner />
          </div>
        ) : (
          courseDetails && (
            <div className="bg-white px-4 py-4 rounded-lg mt-4 flex items-center gap-4">
              {courseDetails.courseImage && (
                <img
                  src={courseDetails.courseImage}
                  alt={courseDetails.courseName}
                  className="h-20 w-20 object-cover rounded-md border border-gray-200"
                />
              )}
              <div>
                <h2 className="text-lg font-medium text-gray-800">
                  {courseDetails.courseName}
                </h2>
                <p className="text-sm text-gray-500 line-clamp-2">
                  {courseDetails.courseDescription}
                </p>
              </div>
            </div>
          )
        )}
        {isLoading ? (
          <div className="grid items-center justify-center my-auto mt-10">
            <Spinner />
          </div>
        ) : (
          <div className="px-2 py-5 rounded-lg grid grid-cols-4 gap-4">
            {data?.data?.course_modules?.length > 0 &&
              data.data.course_modules.map((module) => (
                <div
                  key={module.id}
                  className="cursor-pointer hover:scale-110 ease-in-out duration-300"
                  onClick={() => navigate(`/module/${module.id}`)}
                >
                  <Course
                    name={module.moduleName}
                    description={module.moduleDescription}
                    image={module.moduleImage}
                  />
                </div>
              ))}
          </div>
        )}
        <Modal
          modalOpen={modalOpen}
          toggleModal={toggleModal}
          title="Add Course Module"
        >
          <CreateCourseOverview toggleModal={toggleModal} />
        </Modal>
        <Modal
          modalOpen={editModalOpen}
          toggleModal={toggleEditModal}
          title="Edit Course"
          onClose={toggleEditModal}
        >
          <EditCourse
            toggleModal={toggleEditModal}
            course={courseDetails}
            courseId={courseId}
          />
        </Modal>
      </MainContent>
      <RightBar>
        <Calendar />
      </RightBar>
    </>
  );
}

export default Modules;
